<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("platform_statement.summary") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>

                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="secondary" @click="handleBack">
                        <i class="fa fa-arrow-left mr-2"></i>{{ $t("back") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- Filter Card -->
            <card>
                <template slot="header">
                    <h3 class="mb-0">{{ $t("platform_statement.generate_report") }}</h3>
                </template>

                <div class="row">
                    <div class="col-md-3">
                        <base-input>
                            <el-select
                                v-model="filters.platform_id"
                                :placeholder="$t('platform_statement.platform')"
                                filterable
                            >
                                <el-option
                                    v-for="option in PlatformTable"
                                    :key="option.id"
                                    :label="option.name"
                                    :value="option.id"
                                >
                                </el-option>
                            </el-select>
                        </base-input>
                    </div>

                    <div class="col-md-3">
                        <base-input>
                            <flat-picker
                                slot-scope="{ focus, blur }"
                                @on-open="focus"
                                @on-close="blur"
                                :config="{ allowInput: true }"
                                :placeholder="$t('platform_statement.from_date')"
                                class="form-control datepicker"
                                v-model="filters.date_from"
                            >
                            </flat-picker>
                        </base-input>
                    </div>

                    <div class="col-md-3">
                        <base-input>
                            <flat-picker
                                slot-scope="{ focus, blur }"
                                @on-open="focus"
                                @on-close="blur"
                                :config="{ allowInput: true }"
                                :placeholder="$t('platform_statement.to_date')"
                                class="form-control datepicker"
                                v-model="filters.date_to"
                            >
                            </flat-picker>
                        </base-input>
                    </div>

                    <div class="col-md-3">
                        <base-button
                            type="primary"
                            size="md"
                            :loading="isLoading"
                            @click="handleGenerateReport"
                        >
                            {{ $t("platform_statement.generate_report") }}
                        </base-button>
                    </div>
                </div>
            </card>

            <!-- Summary Statistics -->
            <div class="row" v-if="summary">
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_statements')"
                        type="gradient-red"
                        :sub-title="formatNumber(summary.total_statements)"
                        icon="ni ni-collection"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_orders')"
                        type="gradient-orange"
                        :sub-title="formatNumber(summary.total_orders)"
                        icon="ni ni-bag-17"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.total_sales_amount')"
                        type="gradient-info"
                        :sub-title="formatCurrency(summary.total_sales_amount)"
                        icon="ni ni-money-coins"
                    >
                    </stats-card>
                </div>
                <div class="col-lg-3 col-md-6">
                    <stats-card
                        :title="$t('platform_statement.net_amount')"
                        type="gradient-green"
                        :sub-title="formatCurrency(summary.net_amount)"
                        icon="ni ni-chart-bar-32"
                    >
                    </stats-card>
                </div>
            </div>

            <!-- Revenue Breakdown Card -->
            <card v-if="summary && summary.revenue_breakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-money-coins text-primary mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.revenue_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_revenue')"
                            :value="formatCurrency(summary.revenue_breakdown.total_revenue)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.subtotal_after_seller_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.subtotal_before_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.subtotal_before_discounts)"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.seller_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_subtotal_after_seller_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.refund_subtotal_after_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_subtotal_before_seller_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.refund_subtotal_before_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.refund_of_seller_discounts')"
                            :value="formatCurrency(summary.revenue_breakdown.refund_of_seller_discounts)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <!-- Fee Breakdown Card -->
            <card v-if="summary && summary.fee_breakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-tag text-warning mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.fee_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_platform_fee')"
                            :value="formatCurrency(summary.fee_breakdown.total_platform_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_commission')"
                            :value="formatCurrency(summary.fee_breakdown.total_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_transaction_fee')"
                            :value="formatCurrency(summary.fee_breakdown.total_transaction_fee)"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_shipping_fee')"
                            :value="formatCurrency(summary.fee_breakdown.total_shipping_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_affiliate_commission')"
                            :value="formatCurrency(summary.fee_breakdown.total_affiliate_commission)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_affiliate_shop_ads')"
                            :value="formatCurrency(summary.fee_breakdown.total_affiliate_shop_ads)"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_service_fees')"
                            :value="formatCurrency(summary.fee_breakdown.total_service_fees)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_other_fee')"
                            :value="formatCurrency(summary.fee_breakdown.total_other_fee)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_fees')"
                            :value="formatCurrency(summary.total_fees)"
                            disabled
                        >
                            <template slot="label">
                                <label class="form-control-label font-weight-bold">
                                    {{ $t('platform_statement.total_fees') }}
                                </label>
                            </template>
                        </base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.fee_percentage')"
                            :value="getFeePercentage() + '%'"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <!-- Adjustment Breakdown Card -->
            <card v-if="summary && summary.adjustment_breakdown">
                <template slot="header">
                    <div class="d-flex align-items-center">
                        <i class="ni ni-settings text-info mr-2"></i>
                        <h3 class="mb-0">{{ $t("platform_statement.adjustment_breakdown") }}</h3>
                    </div>
                </template>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.total_adjustment')"
                            :value="formatCurrency(summary.adjustment_breakdown.total_adjustment)"
                            disabled
                        >
                            <template slot="label">
                                <label class="form-control-label font-weight-bold">
                                    {{ $t('platform_statement.total_adjustment') }}
                                </label>
                            </template>
                        </base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.shipping_fee_adjustment')"
                            :value="formatCurrency(summary.adjustment_breakdown.shipping_fee_adjustment)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.logistics_reimbursement')"
                            :value="formatCurrency(summary.adjustment_breakdown.logistics_reimbursement)"
                            disabled
                        ></base-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.platform_compensation')"
                            :value="formatCurrency(summary.adjustment_breakdown.platform_compensation)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.platform_penalty')"
                            :value="formatCurrency(summary.adjustment_breakdown.platform_penalty)"
                            disabled
                        ></base-input>
                    </div>
                    <div class="col-md-4">
                        <base-input
                            :label="$t('platform_statement.other_adjustments')"
                            :value="formatCurrency(summary.adjustment_breakdown.other_adjustments)"
                            disabled
                        ></base-input>
                    </div>
                </div>
            </card>

            <!-- Statement Breakdown Table -->
            <card v-if="summary && summary.statements">
                <template slot="header">
                    <h3 class="mb-0">{{ $t("platform_statement.list") }}</h3>
                </template>

                <el-table
                    :data="summary.statements"
                    row-key="id"
                    header-row-class-name="thead-light"
                >
                    <el-table-column
                        align="left"
                        :label="$t('platform_statement.statement_number')"
                        min-width="150px"
                    >
                        <div slot-scope="{ row }" class="d-flex">
                            <span>{{ row.statement_number }}</span>
                        </div>
                    </el-table-column>

                    <el-table-column
                        align="left"
                        :label="$t('platform_statement.statement_date')"
                        min-width="120px"
                    >
                        <div slot-scope="{ row }" class="d-flex">
                            <span>{{ formatDate(row.statement_date) }}</span>
                        </div>
                    </el-table-column>

                    <el-table-column
                        align="right"
                        :label="$t('platform_statement.total_orders')"
                        min-width="100px"
                    >
                        <div slot-scope="{ row }" class="d-flex justify-content-end">
                            <span>{{ formatNumber(row.total_orders) }}</span>
                        </div>
                    </el-table-column>

                    <el-table-column
                        align="right"
                        :label="$t('platform_statement.total_sales_amount')"
                        min-width="120px"
                    >
                        <div slot-scope="{ row }" class="d-flex justify-content-end">
                            <span>{{ formatCurrency(row.total_sales_amount) }}</span>
                        </div>
                    </el-table-column>

                    <el-table-column
                        align="right"
                        :label="$t('platform_statement.net_amount')"
                        min-width="120px"
                    >
                        <div slot-scope="{ row }" class="d-flex justify-content-end">
                            <span class="text-success font-weight-bold">{{ formatCurrency(row.net_amount) }}</span>
                        </div>
                    </el-table-column>

                    <el-table-column
                        align="right"
                        :label="$t('actions')"
                        min-width="100px"
                    >
                        <div slot-scope="{ row }" class="d-flex justify-content-end">
                            <base-button
                                @click.native="handleViewDetail(row)"
                                class="edit"
                                type="info"
                                size="sm"
                                icon
                            >
                                <i class="fa fa-eye"></i>
                            </base-button>
                        </div>
                    </el-table-column>
                </el-table>
            </card>

            <!-- No Data Message -->
            <card v-if="!summary && !isLoading">
                <div class="text-center py-5">
                    <i class="fa fa-chart-bar fa-3x text-muted mb-3"></i>
                    <p class="text-muted">{{ $t("platform_statement.no_data") }}</p>
                    <p class="text-sm">{{ $t("platform_statement.generate_report") }}</p>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import StatsCard from "@/components/Cards/StatsCard";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";

export default {
    components: {
        flatPicker,
        RouteBreadCrumb,
        StatsCard,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    },
    computed: {
        ...mapGetters({
            summary: "PlatformStatement/getSummary",
            isLoading: "PlatformStatement/isLoading",
            PlatformTable: "PlatformStatement/getPlatformTable"
        })
    },
    data() {
        return {
            filters: {
                platform_id: 2, // Default to TikTok (platform_id=2)
                date_from: "",
                date_to: ""
            }
        };
    },
    mounted() {
        this.filters.date_from = moment().startOf("month").format("YYYY-MM-DD");
        this.filters.date_to = moment().format("YYYY-MM-DD");
    },
    methods: {
        ...mapActions({
            getSummary: "PlatformStatement/getSummary"
        }),
        handleGenerateReport() {
            if (!this.filters.platform_id) {
                swal.fire({
                    title: this.$t("error"),
                    text: this.$t("platform_statement.select_platform"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
                return;
            }

            if (!this.filters.date_from || !this.filters.date_to) {
                swal.fire({
                    title: this.$t("error"),
                    text: this.$t("platform_statement.filter_by_date"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
                return;
            }

            this.getSummary(this.filters);
        },
        handleBack() {
            this.$router.push("/backoffice/platform-statement");
        },
        handleViewDetail(row) {
            this.$router.push(`/backoffice/platform-statement/${row.id}`);
        },
        getTotalFees() {
            if (!this.summary) return 0;
            // Use total_fees from API if available
            if (this.summary.total_fees !== undefined && this.summary.total_fees !== null) {
                return parseFloat(this.summary.total_fees);
            }
            // Fallback: calculate from fee_breakdown if available
            if (this.summary.fee_breakdown) {
                return (
                    parseFloat(this.summary.fee_breakdown.total_platform_fee || 0) +
                    parseFloat(this.summary.fee_breakdown.total_commission || 0) +
                    parseFloat(this.summary.fee_breakdown.total_transaction_fee || 0) +
                    parseFloat(this.summary.fee_breakdown.total_shipping_fee || 0) +
                    parseFloat(this.summary.fee_breakdown.total_affiliate_commission || 0) +
                    parseFloat(this.summary.fee_breakdown.total_affiliate_shop_ads || 0) +
                    parseFloat(this.summary.fee_breakdown.total_service_fees || 0) +
                    parseFloat(this.summary.fee_breakdown.total_other_fee || 0)
                );
            }
            return 0;
        },
        getFeePercentage() {
            if (!this.summary || this.summary.total_sales_amount === 0) return "0.00";
            const totalFees = this.getTotalFees();
            const percentage = (totalFees / this.summary.total_sales_amount) * 100;
            return percentage.toFixed(2);
        },
        formatCurrency(amount) {
            return "฿ " + parseFloat(amount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        formatNumber(num) {
            return parseInt(num || 0).toLocaleString();
        },
        formatDate(date) {
            return date ? moment(date).format("YYYY-MM-DD") : "-";
        }
    }
};
</script>
